import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { PageHero, BusinessCard } from '@/components/Cards';
import { useLanguage } from '@/hooks/useLanguage';
import { ROUTE_PATHS } from '@/lib/index';

const businessAreas = [
  {
    title: 'Global Minerals & Metals',
    titleTh: 'แร่ธาตุและโลหะระดับโลก',
    description: 'Non-ferrous metals & bulk commodities: Aluminum, Copper, Tungsten and more — integrated global sourcing, SGS/CCIC certified, 50k–100k MT/month capacity.',
    descriptionTh: 'โลหะอโลหะและสินค้าโภคภัณฑ์: อะลูมิเนียม ทองแดง ทังสเตน และอื่นๆ — การจัดซื้อทั่วโลกแบบครบวงจร รับรอง SGS/CCIC กำลังการผลิต 50k–100k MT/เดือน',
    image: '/images/real_copper_sheets.webp',
    path: ROUTE_PATHS.BUSINESS_MINERALS,
    icon: '💎',
  },
  {
    title: 'Medical Devices',
    titleTh: 'อุปกรณ์การแพทย์',
    description: 'Class I medical devices for mobility assistance and daily living support: shower chairs, crutches, rollator walkers, and toilet aid equipment.',
    descriptionTh: 'อุปกรณ์การแพทย์ Class I: เก้าอี้อาบน้ำ ไม้เท้า วอล์กเกอร์มีล้อ และอุปกรณ์ช่วยชำระร่างกาย',
    image: '/images/shower_chair_1.jpg',
    path: ROUTE_PATHS.BUSINESS_MEDICAL,
    icon: '🏥',
  },
  {
    title: 'Coffee Products',
    titleTh: 'ผลิตภัณฑ์กาแฟ',
    description: 'Premium Thai highland coffee beans, roasted blends, and processed coffee products for domestic and international markets.',
    descriptionTh: 'เมล็ดกาแฟไทยที่ราบสูงพรีเมียม กาแฟผสมคั่ว และผลิตภัณฑ์กาแฟแปรรูปสำหรับตลาดในประเทศและต่างประเทศ',
    image: '/images/coffee_4.jpeg',
    path: ROUTE_PATHS.BUSINESS_COFFEE,
    icon: '☕',
  },
  {
    title: 'Thai Fruits Export',
    titleTh: 'ส่งออกผลไม้ไทย',
    description: 'Fresh and premium Thai tropical fruits — durian, mango, mangosteen — exported to China via GACC-certified channels.',
    descriptionTh: 'ผลไม้เขตร้อนไทยสดและพรีเมียม ทุเรียน มะม่วง มังคุด ส่งออกไปจีนผ่านช่องทางที่ได้รับการรับรอง GACC',
    image: '/images/fruits_2.jpeg',
    path: ROUTE_PATHS.BUSINESS_FRUITS,
    icon: '🍈',
  },
  {
    title: 'AI Live Streaming',
    titleTh: 'ไลฟ์สดด้วย AI',
    description: 'Next-generation AI-powered live streaming e-commerce platform connecting Thai products with Chinese and global consumers.',
    descriptionTh: 'แพลตฟอร์มอีคอมเมิร์ซไลฟ์สดด้วย AI รุ่นใหม่ เชื่อมต่อสินค้าไทยกับผู้บริโภคชาวจีนและทั่วโลก',
    image: '/images/ailive_7.jpeg',
    path: ROUTE_PATHS.BUSINESS_AI_LIVE,
    icon: '📡',
  },
];

export default function Business() {
  const { t } = useLanguage();

  return (
    <Layout>
      <PageHero
        title="Our Business"
        titleTh="ธุรกิจของเรา"
        subtitle="Metals & bulk commodities, medical devices, coffee, Thai fruits, and AI e-commerce"
        subtitleTh="โลหะและสินค้าโภคภัณฑ์ อุปกรณ์การแพทย์ กาแฟ ผลไม้ไทย และอีคอมเมิร์ซ AI"
        image="/images/hero_4.jpeg"
      />

      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              {t(
                'Yaruwat operates across four complementary business areas, creating synergies and delivering comprehensive solutions for partners worldwide.',
                'ยารุวาทดำเนินงานในสี่ภาคธุรกิจที่เสริมซึ่งกันและกัน สร้างการเสริมกำลังและมอบโซลูชันที่ครอบคลุมสำหรับพันธมิตรทั่วโลก'
              )}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {businessAreas.map((area, i) => (
              <motion.div
                key={area.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="group bg-card rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow border border-border/50"
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={area.image}
                    alt={area.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <span className="absolute top-4 left-4 text-4xl">{area.icon}</span>
                  <h3 className="absolute bottom-4 left-4 text-white text-xl font-bold">
                    {t(area.title, area.titleTh)}
                  </h3>
                </div>
                <div className="p-6">
                  <p className="text-muted-foreground mb-4">{t(area.description, area.descriptionTh)}</p>
                  <Link
                    to={area.path}
                    className="inline-flex items-center gap-2 text-primary font-semibold text-sm hover:gap-3 transition-all"
                  >
                    {t('Learn More', 'เรียนรู้เพิ่มเติม')}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}
